/**
 * Auto-detect the visitor's OS and resolve a download URL.
 *
 * Routes through the Hub's `/downloads/latest/:platform` proxy
 * (see `crates/hub-core/src/downloads.rs`) so the binary can be served from
 * a **private** GitHub repo without the browser ever seeing the PAT, and so
 * the frontend never has to know the current version tag — the proxy
 * resolves "latest installer for this platform" to the current versioned
 * asset (e.g. `fabricos-v0.1.0-linux.deb`).
 *
 * The Hub host is the same one the SPA already talks to for auth/telemetry:
 * `VITE_HUB_PROXY` in the cloud build, embedded scheme in Tauri.
 */

import { HUB_HTTP_BASE } from './hubUrl';

export type DownloadOS = 'mac' | 'win' | 'linux' | 'android' | 'unknown';
/** Back-compat alias — older callers still import `DesktopOS`. */
export type DesktopOS = DownloadOS;

export interface DownloadTarget {
  os: DownloadOS;
  /** Human-friendly OS name for the button label. */
  label: string;
  /** Direct download URL pointing at the Hub proxy. */
  url: string;
  /** Marketing tag that shows the bundle format below the OS name. */
  fileLabel: string;
}

const PATTERNS: Record<
  DownloadOS,
  { match: RegExp; platform: string; label: string; fileLabel: string }
> = {
  // Android MUST be tested before Linux — every Android UA contains the
  // substring "Linux; Android <ver>". Same logic for iOS vs macOS (iPhone
  // / iPad currently match the mac pattern; that's intentional — iOS users
  // get the desktop installer until we ship a TestFlight build).
  android: {
    match: /android/i,
    platform: 'android',
    label: 'Android',
    fileLabel: '.apk',
  },
  mac: {
    match: /(mac|darwin|iphone|ipad)/i,
    platform: 'macos',
    label: 'macOS',
    fileLabel: '.dmg',
  },
  win: {
    match: /windows/i,
    platform: 'windows',
    label: 'Windows',
    fileLabel: '.exe',
  },
  linux: {
    match: /linux/i,
    platform: 'linux',
    label: 'Linux',
    fileLabel: '.deb',
  },
  unknown: {
    match: /^$/,
    platform: '',
    label: 'Desktop',
    fileLabel: '',
  },
};

export function detectOS(): DownloadOS {
  if (typeof navigator === 'undefined') return 'unknown';
  // userAgentData is the modern spec; userAgent is the universal fallback.
  const hint =
    (navigator as Navigator & { userAgentData?: { platform: string } }).userAgentData?.platform ??
    navigator.userAgent ??
    '';
  // Order matters — Android UAs include "Linux", so Android must come first.
  if (PATTERNS.android.match.test(hint)) return 'android';
  if (PATTERNS.mac.match.test(hint)) return 'mac';
  if (PATTERNS.win.match.test(hint)) return 'win';
  if (PATTERNS.linux.match.test(hint)) return 'linux';
  return 'unknown';
}

export function resolveDownload(): DownloadTarget {
  const os = detectOS();
  const p = PATTERNS[os];
  // For unknown OS, send to the bare path which 400s — better signal than
  // guessing wrong and serving a binary the user can't run.
  const url =
    os === 'unknown'
      ? `${HUB_HTTP_BASE}/downloads/latest/`
      : `${HUB_HTTP_BASE}/downloads/latest/${p.platform}`;
  return { os, label: p.label, fileLabel: p.fileLabel, url };
}
